import * as THREE from 'three';
import { Chunk } from './chunk';
import { buildChunkMesh } from './chunk-mesh-builder';
import { TerrainGenerator } from '../generation/terrain-generator';
import { BlockId } from '@shared/block-ids';
import { CHUNK_WIDTH, CHUNK_DEPTH, CHUNK_HEIGHT } from '@shared/chunk-constants';
import { isSolidBlock } from './block-registry';
import { chunkKey, worldToChunkCoord, worldToLocalCoord } from '../utils/chunk-coord';
import { RENDER_DISTANCE } from '../constants';
import { saveChunk, loadChunk } from '../storage/local-db';

interface ChunkEntry {
  chunk: Chunk;
  solidMesh: THREE.Mesh | null;
  cutoutMesh: THREE.Mesh | null;
  transparentMesh: THREE.Mesh | null;
}

export class World {
  private chunks = new Map<string, ChunkEntry>();
  private generator: TerrainGenerator;
  private scene: THREE.Scene;
  private solidMaterial: THREE.MeshLambertMaterial;
  private cutoutMaterial: THREE.MeshLambertMaterial;
  private transparentMaterial: THREE.MeshLambertMaterial;
  private genQueue: [number, number][] = [];
  private _seed: number;
  private _worldId: string | null = null;
  private modifiedChunks = new Set<string>();
  private saveTimer = 0;

  constructor(scene: THREE.Scene, seed: number, atlasTexture: THREE.Texture) {
    this.scene = scene;
    this._seed = seed;
    this.generator = new TerrainGenerator(seed);

    atlasTexture.magFilter = THREE.NearestFilter;
    atlasTexture.minFilter = THREE.NearestFilter;
    atlasTexture.colorSpace = THREE.SRGBColorSpace;

    this.solidMaterial = new THREE.MeshLambertMaterial({
      map: atlasTexture,
      vertexColors: true,
      side: THREE.FrontSide,
    });

    // Alpha-tested cutout material for leaves, flowers, torches
    this.cutoutMaterial = new THREE.MeshLambertMaterial({
      map: atlasTexture,
      vertexColors: true,
      side: THREE.DoubleSide,
      alphaTest: 0.5,
      transparent: false,
    });

    // Truly translucent material for glass, water, ice
    this.transparentMaterial = new THREE.MeshLambertMaterial({
      map: atlasTexture,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
  }

  get seed(): number {
    return this._seed;
  }

  set worldId(id: string | null) {
    this._worldId = id;
  }

  get worldId(): string | null {
    return this._worldId;
  }

  getBlock(wx: number, wy: number, wz: number): BlockId {
    if (wy < 0 || wy >= CHUNK_HEIGHT) return BlockId.AIR;
    const [cx, cz] = worldToChunkCoord(wx, wz);
    const entry = this.chunks.get(chunkKey(cx, cz));
    if (!entry) return BlockId.AIR;
    const [lx, lz] = worldToLocalCoord(wx, wz);
    return entry.chunk.getBlock(lx, wy, lz);
  }

  setBlock(wx: number, wy: number, wz: number, id: BlockId): void {
    if (wy < 0 || wy >= CHUNK_HEIGHT) return;
    const [cx, cz] = worldToChunkCoord(wx, wz);
    const entry = this.chunks.get(chunkKey(cx, cz));
    if (!entry) return;
    const [lx, lz] = worldToLocalCoord(wx, wz);
    entry.chunk.setBlock(lx, wy, lz, id);
    this.modifiedChunks.add(chunkKey(cx, cz));

    // Mark neighboring chunks dirty if at boundary
    if (lx === 0) this.markDirty(cx - 1, cz);
    if (lx === CHUNK_WIDTH - 1) this.markDirty(cx + 1, cz);
    if (lz === 0) this.markDirty(cx, cz - 1);
    if (lz === CHUNK_DEPTH - 1) this.markDirty(cx, cz + 1);

    this.rebuildChunkMesh(cx, cz);
  }

  isSolid(wx: number, wy: number, wz: number): boolean {
    return isSolidBlock(this.getBlock(wx, wy, wz));
  }

  private markDirty(cx: number, cz: number): void {
    const entry = this.chunks.get(chunkKey(cx, cz));
    if (entry) {
      entry.chunk.dirty = true;
      this.rebuildChunkMesh(cx, cz);
    }
  }

  updateChunks(playerX: number, playerZ: number): void {
    const [pcx, pcz] = worldToChunkCoord(Math.floor(playerX), Math.floor(playerZ));

    // Queue new chunks to generate
    for (let dx = -RENDER_DISTANCE; dx <= RENDER_DISTANCE; dx++) {
      for (let dz = -RENDER_DISTANCE; dz <= RENDER_DISTANCE; dz++) {
        if (dx * dx + dz * dz > RENDER_DISTANCE * RENDER_DISTANCE) continue;
        const cx = pcx + dx;
        const cz = pcz + dz;
        const key = chunkKey(cx, cz);
        if (!this.chunks.has(key)) {
          this.genQueue.push([cx, cz]);
        }
      }
    }

    // Sort by distance to player
    this.genQueue.sort((a, b) => {
      const da = (a[0] - pcx) ** 2 + (a[1] - pcz) ** 2;
      const db = (b[0] - pcx) ** 2 + (b[1] - pcz) ** 2;
      return da - db;
    });

    // Generate a few per frame
    const maxPerFrame = 2;
    let generated = 0;
    while (this.genQueue.length > 0 && generated < maxPerFrame) {
      const [cx, cz] = this.genQueue.shift()!;
      const key = chunkKey(cx, cz);
      if (this.chunks.has(key)) continue;

      const chunk = new Chunk(cx, cz);
      this.generator.generate(chunk);
      this.chunks.set(key, { chunk, solidMesh: null, cutoutMesh: null, transparentMesh: null });
      this.rebuildChunkMesh(cx, cz);

      // Try to load saved modifications in background
      if (this._worldId) {
        this.tryLoadChunk(cx, cz).then(saved => {
          if (saved) {
            chunk.blocks.set(saved);
            chunk.dirty = true;
            this.rebuildChunkMesh(cx, cz);
          }
        });
      }
      generated++;
    }

    // Unload distant chunks
    for (const [key, entry] of this.chunks) {
      const dx = entry.chunk.cx - pcx;
      const dz = entry.chunk.cz - pcz;
      if (dx * dx + dz * dz > (RENDER_DISTANCE + 2) * (RENDER_DISTANCE + 2)) {
        if (entry.solidMesh) {
          this.scene.remove(entry.solidMesh);
          entry.solidMesh.geometry.dispose();
        }
        if (entry.cutoutMesh) {
          this.scene.remove(entry.cutoutMesh);
          entry.cutoutMesh.geometry.dispose();
        }
        if (entry.transparentMesh) {
          this.scene.remove(entry.transparentMesh);
          entry.transparentMesh.geometry.dispose();
        }
        this.chunks.delete(key);
      }
    }

    // Rebuild dirty chunks
    for (const [, entry] of this.chunks) {
      if (entry.chunk.dirty) {
        this.rebuildChunkMesh(entry.chunk.cx, entry.chunk.cz);
      }
    }
  }

  private rebuildChunkMesh(cx: number, cz: number): void {
    const key = chunkKey(cx, cz);
    const entry = this.chunks.get(key);
    if (!entry) return;

    const neighbors = {
      px: this.chunks.get(chunkKey(cx + 1, cz))?.chunk,
      nx: this.chunks.get(chunkKey(cx - 1, cz))?.chunk,
      pz: this.chunks.get(chunkKey(cx, cz + 1))?.chunk,
      nz: this.chunks.get(chunkKey(cx, cz - 1))?.chunk,
    };

    const { solid, cutout, transparent } = buildChunkMesh(entry.chunk, neighbors);

    // Remove old meshes
    if (entry.solidMesh) {
      this.scene.remove(entry.solidMesh);
      entry.solidMesh.geometry.dispose();
      entry.solidMesh = null;
    }
    if (entry.cutoutMesh) {
      this.scene.remove(entry.cutoutMesh);
      entry.cutoutMesh.geometry.dispose();
      entry.cutoutMesh = null;
    }
    if (entry.transparentMesh) {
      this.scene.remove(entry.transparentMesh);
      entry.transparentMesh.geometry.dispose();
      entry.transparentMesh = null;
    }

    const worldX = cx * CHUNK_WIDTH;
    const worldZ = cz * CHUNK_DEPTH;

    if (solid) {
      entry.solidMesh = new THREE.Mesh(solid, this.solidMaterial);
      entry.solidMesh.position.set(worldX, 0, worldZ);
      entry.solidMesh.frustumCulled = true;
      this.scene.add(entry.solidMesh);
    }

    if (cutout) {
      entry.cutoutMesh = new THREE.Mesh(cutout, this.cutoutMaterial);
      entry.cutoutMesh.position.set(worldX, 0, worldZ);
      entry.cutoutMesh.frustumCulled = true;
      this.scene.add(entry.cutoutMesh);
    }

    if (transparent) {
      entry.transparentMesh = new THREE.Mesh(transparent, this.transparentMaterial);
      entry.transparentMesh.position.set(worldX, 0, worldZ);
      entry.transparentMesh.frustumCulled = true;
      entry.transparentMesh.renderOrder = 1;
      this.scene.add(entry.transparentMesh);
    }

    entry.chunk.dirty = false;
  }

  async saveModifiedChunks(): Promise<void> {
    if (!this._worldId || this.modifiedChunks.size === 0) return;
    const toSave = [...this.modifiedChunks];
    this.modifiedChunks.clear();
    for (const key of toSave) {
      const entry = this.chunks.get(key);
      if (entry) {
        await saveChunk(this._worldId, entry.chunk.cx, entry.chunk.cz, entry.chunk.blocks);
      }
    }
  }

  async tryLoadChunk(cx: number, cz: number): Promise<Uint8Array | null> {
    if (!this._worldId) return null;
    return loadChunk(this._worldId, cx, cz);
  }

  tickSave(dt: number): void {
    this.saveTimer += dt;
    if (this.saveTimer >= 10) { // auto-save every 10 seconds
      this.saveTimer = 0;
      this.saveModifiedChunks();
    }
  }

  getSpawnHeight(wx: number, wz: number): number {
    for (let y = CHUNK_HEIGHT - 1; y >= 0; y--) {
      if (this.isSolid(wx, y, wz)) {
        return y + 1;
      }
    }
    return 80;
  }

  getLoadedChunkCount(): number {
    return this.chunks.size;
  }

  dispose(): void {
    for (const [, entry] of this.chunks) {
      if (entry.solidMesh) {
        this.scene.remove(entry.solidMesh);
        entry.solidMesh.geometry.dispose();
      }
      if (entry.cutoutMesh) {
        this.scene.remove(entry.cutoutMesh);
        entry.cutoutMesh.geometry.dispose();
      }
      if (entry.transparentMesh) {
        this.scene.remove(entry.transparentMesh);
        entry.transparentMesh.geometry.dispose();
      }
    }
    this.chunks.clear();
    this.solidMaterial.dispose();
    this.cutoutMaterial.dispose();
    this.transparentMaterial.dispose();
  }
}
